<template>
  <div class="order-book">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <div>
          <v-icon class="mr-2">mdi-book-open-variant</v-icon>
          Livro de Ordens
        </div>
        <v-btn-toggle v-model="visualizacao" mandatory density="comfortable" variant="outlined">
          <v-btn value="ordens">Ordens</v-btn>
          <v-btn value="historico">Histórico</v-btn>
        </v-btn-toggle>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <v-card-text class="pa-0">
        <v-window v-model="visualizacao">
          <!-- Visualização de ordens abertas -->
          <v-window-item value="ordens">
            <div class="d-flex flex-column">
              <!-- Cabeçalho da tabela de ordens -->
              <div class="d-flex order-header px-4 py-2 bg-grey-lighten-4">
                <div class="order-cell-price">{{ t('dex.price') }}</div>
                <div class="order-cell-amount">{{ t('dex.amount') }}</div>
                <div class="order-cell-total">{{ t('dex.total') }}</div>
                <div class="order-cell-action">Ação</div>
              </div>
              
              <!-- Ordens de Venda (ask) - em ordem decrescente de preço -->
              <div class="order-sells">
                <div 
                  v-for="(order, index) in sortedSellOrders" 
                  :key="'sell-'+index"
                  class="d-flex order-row px-4 py-2 order-sell"
                  :class="{ 'order-row-hover': !isUserOrder(order) }"
                  @click="!isUserOrder(order) && handleOrderClick(order, 'buy')"
                >
                  <div class="order-cell-price text-error">{{ formatPrice(order.price) }}</div>
                  <div class="order-cell-amount">{{ formatAmount(order.amount) }}</div>
                  <div class="order-cell-total">{{ formatTotal(order.price * order.amount) }}</div>
                  <div class="order-cell-action">
                    <template v-if="isUserOrder(order)">
                      <v-btn
                        size="x-small"
                        variant="text"
                        color="error"
                        icon="mdi-close-circle-outline"
                        @click.stop="cancelOrder(order)"
                      >
                      </v-btn>
                    </template>
                    <template v-else>
                      <v-btn
                        size="x-small"
                        color="success"
                        variant="tonal"
                        @click.stop="confirmOrder(order, 'buy')"
                      >
                        Comprar
                      </v-btn>
                    </template>
                  </div>
                </div>
              </div>
              
              <!-- Preço de mercado atual -->
              <div class="current-price-bar d-flex justify-space-between align-center px-4 py-3">
                <div class="text-subtitle-1 font-weight-bold">{{ formatPrice(currentPrice) }}</div>
                <div 
                  class="text-caption font-weight-medium"
                  :class="priceChangePositive ? 'text-success' : 'text-error'"
                >
                  {{ priceChangePositive ? '+' : '' }}{{ formatPrice(priceChange) }} 
                  ({{ formatPercentage(priceChangePercent) }})
                </div>
              </div>
              
              <!-- Ordens de Compra (bid) - em ordem decrescente de preço -->
              <div class="order-buys">
                <div 
                  v-for="(order, index) in sortedBuyOrders" 
                  :key="'buy-'+index"
                  class="d-flex order-row px-4 py-2 order-buy"
                  :class="{ 'order-row-hover': !isUserOrder(order) }"
                  @click="!isUserOrder(order) && handleOrderClick(order, 'sell')"
                >
                  <div class="order-cell-price text-success">{{ formatPrice(order.price) }}</div>
                  <div class="order-cell-amount">{{ formatAmount(order.amount) }}</div>
                  <div class="order-cell-total">{{ formatTotal(order.price * order.amount) }}</div>
                  <div class="order-cell-action">
                    <template v-if="isUserOrder(order)">
                      <v-btn
                        size="x-small"
                        variant="text"
                        color="error"
                        icon="mdi-close-circle-outline"
                        @click.stop="cancelOrder(order)"
                      >
                      </v-btn>
                    </template>
                    <template v-else>
                      <v-btn
                        size="x-small"
                        color="error"
                        variant="tonal"
                        @click.stop="handleOrderClick(order, 'sell')"
                      >
                        Vender
                      </v-btn>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </v-window-item>
          
          <!-- Histórico de transações -->
          <v-window-item value="historico">
            <div class="d-flex flex-column">
              <!-- Cabeçalho da tabela de histórico -->
              <div class="d-flex order-header px-4 py-2 bg-grey-lighten-4">
                <div class="history-cell-time">Hora</div>
                <div class="history-cell-price">{{ t('dex.price') }}</div>
                <div class="history-cell-amount">{{ t('dex.amount') }}</div>
                <div class="history-cell-type">{{ t('gov.type') }}</div>
              </div>
              
              <!-- Transações do histórico (mais recentes primeiro) -->
              <div class="order-history">
                <div 
                  v-for="(tx, index) in recentTrades" 
                  :key="'tx-'+index"
                  class="d-flex order-row px-4 py-2"
                  :class="tx.type === 'buy' ? 'order-buy' : 'order-sell'"
                >
                  <div class="history-cell-time">{{ formatTime(tx.timestamp) }}</div>
                  <div 
                    class="history-cell-price"
                    :class="tx.type === 'buy' ? 'text-success' : 'text-error'"
                  >
                    {{ formatPrice(tx.price) }}
                  </div>
                  <div class="history-cell-amount">{{ formatAmount(tx.amount) }}</div>
                  <div class="history-cell-type">
                    <v-chip
                      size="x-small"
                      :color="tx.type === 'buy' ? 'success' : 'error'"
                      text-color="white"
                    >
                      {{ tx.type === 'buy' ? 'Compra' : 'Venda' }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>
    
    <!-- Diálogo para confirmar execução de ordem -->
    <v-dialog v-model="confirmDialog" max-width="400">
      <v-card>
        <v-card-title>
          <v-icon class="mr-2" :color="orderAction === 'buy' ? 'success' : 'error'">
            {{ orderAction === 'buy' ? 'mdi-cash-plus' : 'mdi-cash-minus' }}
          </v-icon>
          {{ orderAction === 'buy' ? 'Confirmar Compra' : 'Confirmar Venda' }}
        </v-card-title>
        <v-card-text v-if="selectedOrder">
          <p class="mb-4">
            Você está prestes a 
            <span class="font-weight-bold">
              {{ orderAction === 'buy' ? 'comprar' : 'vender' }}
            </span>
            o seguinte:
          </p>
          
          <v-list>
            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-cash" class="mr-2"></v-icon>
              </template>
              <v-list-item-title>
                Preço: {{ formatPrice(selectedOrder.price) }} {{ quoteToken }}
              </v-list-item-title>
            </v-list-item>
            
            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-cube-outline" class="mr-2"></v-icon>
              </template>
              <v-list-item-title>
                Quantidade: {{ formatAmount(selectedOrder.amount) }} {{ baseToken }}
              </v-list-item-title>
            </v-list-item>
            
            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-calculator" class="mr-2"></v-icon>
              </template>
              <v-list-item-title>
                Total: {{ formatTotal(selectedOrder.price * selectedOrder.amount) }} {{ quoteToken }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
          
          <v-alert
            density="compact"
            type="info"
            variant="tonal"
            icon="mdi-information-outline"
            class="mt-4"
          >
            Esta é uma transação descentralizada direta entre você e o criador da ordem.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="confirmDialog = false">Cancelar</v-btn>
          <v-btn 
            :color="orderAction === 'buy' ? 'success' : 'error'"
            variant="tonal"
            @click="executeOrder"
          >
            {{ orderAction === 'buy' ? 'Comprar' : 'Vender' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useDexStore } from '@/stores/dex'
import { useAppStore } from '@/stores/app'

export default {
  name: 'OrderBook',
  
  props: {
    pairSymbol: {
      type: String,
      required: true
    },
    baseToken: {
      type: String,
      required: true
    },
    quoteToken: {
      type: String,
      required: true
    }
  },
  
  data() {
    return {
      visualizacao: 'ordens',
      currentPrice: 2.5,
      priceChange: 0.15,
      priceChangePercent: 6.38,
      priceChangePositive: true,
      
      // Diálogo de confirmação
      confirmDialog: false,
      selectedOrder: null,
      orderAction: null, // 'buy' ou 'sell'
      
      // Mock de ordens de compra
      buyOrders: [
        { id: 'b1', userId: 'user1', type: 'buy', price: 2.48, amount: 350, timestamp: new Date() },
        { id: 'b2', userId: 'current', type: 'buy', price: 2.45, amount: 125, timestamp: new Date() },
        { id: 'b3', userId: 'user2', type: 'buy', price: 2.42, amount: 430, timestamp: new Date() },
        { id: 'b4', userId: 'user3', type: 'buy', price: 2.40, amount: 280, timestamp: new Date() },
        { id: 'b5', userId: 'user4', type: 'buy', price: 2.39, amount: 560, timestamp: new Date() },
        { id: 'b6', userId: 'user5', type: 'buy', price: 2.35, amount: 710, timestamp: new Date() }
      ],
      
      // Mock de ordens de venda
      sellOrders: [
        { id: 's1', userId: 'user6', type: 'sell', price: 2.52, amount: 210, timestamp: new Date() },
        { id: 's2', userId: 'user7', type: 'sell', price: 2.55, amount: 180, timestamp: new Date() },
        { id: 's3', userId: 'current', type: 'sell', price: 2.58, amount: 95, timestamp: new Date() },
        { id: 's4', userId: 'user8', type: 'sell', price: 2.60, amount: 320, timestamp: new Date() },
        { id: 's5', userId: 'user9', type: 'sell', price: 2.63, amount: 145, timestamp: new Date() },
        { id: 's6', userId: 'user10', type: 'sell', price: 2.65, amount: 270, timestamp: new Date() }
      ],
      
      // Mock de histórico de transações
      recentTrades: [
        { id: 't1', price: 2.51, amount: 120, type: 'buy', timestamp: this.minutosAtras(2) },
        { id: 't2', price: 2.53, amount: 85, type: 'sell', timestamp: this.minutosAtras(8) },
        { id: 't3', price: 2.49, amount: 210, type: 'buy', timestamp: this.minutosAtras(15) },
        { id: 't4', price: 2.52, amount: 45, type: 'sell', timestamp: this.minutosAtras(22) },
        { id: 't5', price: 2.47, amount: 180, type: 'buy', timestamp: this.minutosAtras(34) },
        { id: 't6', price: 2.51, amount: 95, type: 'sell', timestamp: this.minutosAtras(41) },
        { id: 't7', price: 2.48, amount: 130, type: 'buy', timestamp: this.minutosAtras(52) },
        { id: 't8', price: 2.50, amount: 75, type: 'sell', timestamp: this.minutosAtras(65) },
        { id: 't9', price: 2.45, amount: 220, type: 'buy', timestamp: this.minutosAtras(78) },
        { id: 't10', price: 2.48, amount: 110, type: 'sell', timestamp: this.minutosAtras(90) }
      ]
    }
  },
  
  computed: {
    dexStore() {
      return useDexStore()
    },
    
    appStore() {
      return useAppStore()
    },
    
    sortedBuyOrders() {
      // Ordens de compra em ordem decrescente de preço
      return [...this.buyOrders].sort((a, b) => b.price - a.price)
    },
    
    sortedSellOrders() {
      // Ordens de venda em ordem crescente de preço
      return [...this.sellOrders].sort((a, b) => a.price - b.price)
    }
  },
  
  methods: {
    formatPrice(price) {
      return price.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6
      })
    },
    
    formatAmount(amount) {
      return amount.toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 6
      })
    },
    
    formatTotal(total) {
      return total.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    formatPercentage(percent) {
      return percent.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + '%'
    },
    
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    minutosAtras(minutes) {
      const date = new Date()
      date.setMinutes(date.getMinutes() - minutes)
      return date
    },
    
    isUserOrder(order) {
      return order.isUserOrder
    },
    
    confirmOrder(order, action) {
      // Abre um modal de confirmação
      this.$emit('order-confirm', { order, action })
    },
    
    handleOrderClick(order, action) {
      // Para manter compatibilidade com código existente
      this.confirmOrder(order, action)
    },
    
    cancelOrder(order) {
      // Implementação para cancelar uma ordem do usuário
      const orderType = order.type
      const orderList = orderType === 'buy' ? this.buyOrders : this.sellOrders
      
      const index = orderList.findIndex(o => o.id === order.id)
      if (index !== -1) {
        if (orderType === 'buy') {
          this.buyOrders.splice(index, 1)
        } else {
          this.sellOrders.splice(index, 1)
        }
        
        this.appStore.showSnackbar({
          text: `Ordem de ${orderType === 'buy' ? 'compra' : 'venda'} cancelada com sucesso`,
          color: 'success'
        })
      }
    },
    
    executeOrder() {
      if (!this.selectedOrder) return
      
      // Simulação de execução da ordem
      const price = this.selectedOrder.price
      const amount = this.selectedOrder.amount
      const total = price * amount
      const action = this.orderAction
      
      // Adicionar a transação ao histórico
      const newTrade = {
        id: `t${this.recentTrades.length + 1}`,
        price,
        amount,
        type: action,
        timestamp: new Date()
      }
      
      this.recentTrades.unshift(newTrade)
      
      // Remover a ordem executada
      if (action === 'buy') {
        const index = this.sellOrders.findIndex(o => o.id === this.selectedOrder.id)
        if (index !== -1) {
          this.sellOrders.splice(index, 1)
        }
      } else {
        const index = this.buyOrders.findIndex(o => o.id === this.selectedOrder.id)
        if (index !== -1) {
          this.buyOrders.splice(index, 1)
        }
      }
      
      // Fechar o diálogo
      this.confirmDialog = false
      
      // Mostrar notificação
      this.appStore.showSnackbar({
        text: `${action === 'buy' ? 'Compra' : 'Venda'} de ${this.formatAmount(amount)} ${this.baseToken} por ${this.formatPrice(price)} ${this.quoteToken} concluída`,
        color: 'success'
      })
      
      // Atualizar o preço atual para refletir a última transação
      this.currentPrice = price
    }
  }
}
</script>

<style scoped>
.order-book {
  width: 100%;
}

.order-header {
  font-weight: bold;
  font-size: 0.8rem;
  opacity: 0.7;
}

.order-cell-price {
  width: 25%;
  text-align: left;
}

.order-cell-amount {
  width: 25%;
  text-align: right;
}

.order-cell-total {
  width: 35%;
  text-align: right;
}

.order-cell-action {
  width: 15%;
  text-align: center;
}

.history-cell-time {
  width: 20%;
  text-align: left;
}

.history-cell-price {
  width: 25%;
  text-align: left;
}

.history-cell-amount {
  width: 35%;
  text-align: right;
}

.history-cell-type {
  width: 20%;
  text-align: center;
}

.order-row {
  transition: background-color 0.2s;
}

.order-row-hover:hover {
  background-color: rgba(0, 0, 0, 0.05);
  cursor: pointer;
}

.order-buy {
  border-left: 4px solid transparent;
}

.order-sell {
  border-left: 4px solid transparent;
}

.current-price-bar {
  background-color: rgba(0, 0, 0, 0.03);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
</style>
